import useFamousProductVariantMatches from '@/api/erp-integrations/famous/useFamousProductVariantMatches';
import useCreateProductVariant from '@/api/inventory-management/products/useCreateProductVariant';
import { CheckCircleIcon, InfoIcon, SpinnerIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { Input } from '../ui/input';
import VirtualizedVariantList from '@/pages/authenticated/core/sales-order-drafts/components/VirtualizedVariantList';
import { Alert } from '@/components/common/alert';
import { Button } from '@/components/ui/button';

export function MatchingVariantsInfo({
  selectedProductId,
  selectedAttributes,
  selectedVariantId,
  onSelectVariant,
  customerId,
}: {
  selectedProductId?: string;
  selectedAttributes: Record<string, string | null>;
  selectedVariantId?: string;
  onSelectVariant: (variantId: string) => void;
  customerId?: string;
}) {
  // Variant filter state
  const [variantFilter, setVariantFilter] = useState<string>('');
  const {
    data: variantMatchesData,
    isLoading: isLoadingVariants,
    error: variantMatchesError,
  } = useFamousProductVariantMatches(selectedProductId, {
    originAttributes:
      Object.keys(selectedAttributes).length > 0
        ? selectedAttributes
        : undefined,
    customerId,
  });

  const createProductVariant = useCreateProductVariant((response) => {
    // After creating the variant, automatically select it
    onSelectVariant(response.productVariant.id);
  });

  const handleCreateNewVariant = () => {
    if (!selectedProductId) {
      console.error('No product selected');
      return;
    }

    // Filter out null values from selectedAttributes
    const filteredAttributes = Object.fromEntries(
      Object.entries(selectedAttributes).filter(([, value]) => value !== null),
    );

    createProductVariant.mutate({
      productId: selectedProductId,
      attributes: filteredAttributes,
      active: true,
    });
  };

  if (isLoadingVariants) {
    return (
      <div className="flex items-center gap-2">
        <SpinnerIcon className="size-4 animate-spin" />
        <span className="text-sm text-muted-foreground">
          Finding matching variants...
        </span>
      </div>
    );
  }

  if (variantMatchesError) {
    return (
      <Alert
        title="Error loading variants"
        description="Please try again later"
        type="error"
      />
    );
  }

  if (variantMatchesData?.variants && variantMatchesData.variants.length > 0) {
    const variants = variantMatchesData.variants;

    // If there are too many variants, just show a count
    if (variants.length > 2500) {
      return (
        <div className="bg-blue-100 p-2 rounded border border-blue-200">
          <div className="flex items-center">
            <InfoIcon />
            <div>
              <span className="text-sm font-medium">
                {variants.length} matching variants found
              </span>
              <span className="text-sm text-muted-foreground">
                Please refine your attribute selections to narrow down the
                options
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Show selectable variants list
    return (
      <div className="mt-2 mb-1">
        <div className="flex justify-between items-center mb-1.5 px-0.5">
          <div className="flex items-center gap-1">
            <CheckCircleIcon className="size-4 text-green-500" />
            <span className="text-base font-medium">
              {variants.length === 1
                ? 'One match found'
                : `${variants.length} matching variants found`}
            </span>
          </div>

          {/* Filter input */}
          <div className="w-64">
            <Input
              className="w-full"
              placeholder="Filter variants..."
              value={variantFilter}
              onChange={(e) => setVariantFilter(e.target.value)}
            />
          </div>
        </div>

        {(() => {
          // Filter variants based on the filter text
          const filteredVariants = variants.filter((variant) => {
            if (!variantFilter) return true;

            const filterLower = variantFilter.toLowerCase();

            // Filter by SKU
            if (variant.sku?.toLowerCase().includes(filterLower)) {
              return true;
            }

            // Filter by ID
            if (variant.id.toLowerCase().includes(filterLower)) {
              return true;
            }

            // Filter by NAMEINVC if available in originMetadata
            if (
              variant.originMetadata &&
              typeof variant.originMetadata === 'object'
            ) {
              const metadata = variant.originMetadata as unknown as {
                NAMEINVC?: string;
                PRODUCT_ID?: string;
              };
              if (metadata.NAMEINVC && typeof metadata.NAMEINVC === 'string') {
                if (metadata.NAMEINVC.toLowerCase().includes(filterLower)) {
                  return true;
                }
              }
              if (
                metadata.PRODUCT_ID &&
                typeof metadata.PRODUCT_ID === 'string'
              ) {
                if (metadata.PRODUCT_ID.toLowerCase().includes(filterLower)) {
                  return true;
                }
              }
            }

            return false;
          });

          // Update the count message
          const countMessage =
            filteredVariants.length === 0
              ? 'No variants match your filter'
              : filteredVariants.length === 1
                ? '1 variant matches your filter'
                : `${filteredVariants.length} variants match your filter`;

          return (
            <>
              {variantFilter && (
                <div className="mb-1.5 mt-[-1.5] px-0.5 text-right">
                  <span className="text-sm text-muted-foreground">
                    {countMessage}
                  </span>
                </div>
              )}

              <VirtualizedVariantList
                variants={filteredVariants}
                selectedVariantId={selectedVariantId}
                onSelect={onSelectVariant}
                maxHeight={300}
              />
            </>
          );
        })()}
      </div>
    );
  }

  if (Object.keys(selectedAttributes).length > 0) {
    return (
      <div className="flex gap-2 items-center">
        <Alert
          title="No matching variants found with these attributes"
          type="error"
        />
        <Button
          variant="outline"
          onClick={handleCreateNewVariant}
          disabled={createProductVariant.isLoading || !selectedProductId}
        >
          {createProductVariant.isLoading ? (
            <>
              <SpinnerIcon className="size-4 animate-spin mr-2" />
              Creating...
            </>
          ) : (
            'Create new variant'
          )}
        </Button>
      </div>
    );
  }

  return null;
}
